import { Injectable } from '@angular/core';
import { BaseClient } from '../common/client/base.client';
import { SvcClients } from '../common/client/SvcClients';

@Injectable({
  providedIn: 'root',
})
export class TlmClient extends BaseClient {
  constructor() {
    super();
  }


  acknowledgeAlertByRef(message: string, customerCode: number, kind: string, refId: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMAlert/AcknowledgeByRef?customerCode=${customerCode}`,
      { kind, refId },
      message, undefined, true, null, true
    );
  }

  acknowledgeAllAlertsForGroup(message: string, companyGroupCode: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMAlert/AcknowledgeAll?companyGroupCode=${companyGroupCode}`,
      {},
      message, undefined, true, null, true
    );
  }

  getActiveTimecards(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/GetActive?customerCode=${customerCode}`,
      message
    );
  }

  // FENIKS US-5P.101 - the day's cards grouped by employee (totals of closed cards, cards, lock flag).
  getTimecardSummaryForBusinessDay(message: string, customerCode: number, businessDate: Date) {
    const day = this.datepipe.transform(businessDate, 'yyyy-MM-dd');
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/GetTimecardSummaryForBusinessDay?customerCode=${customerCode}&businessDate=${day}`,
      message, undefined, true, null, true
    );
  }

  getTimeDataSummary(message: string, customerCode: number, businessDate: Date) {
    const day = this.datepipe.transform(businessDate, 'yyyy-MM-dd');
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/GetSummaryForBusinessDay?customerCode=${customerCode}&businessDate=${day}`,
      message
    );
  }

  getTimeDataSummaryWeekly(message: string, customerCode: number, dateFrom: Date, dateTo: Date) {
    const from = this.datepipe.transform(dateFrom, 'yyyy-MM-dd');
    const to = this.datepipe.transform(dateTo, 'yyyy-MM-dd');
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/GetSummaryWeekly?customerCode=${customerCode}&dateFrom=${from}&dateTo=${to}`,
      message
    );
  }

  getTimeDataSummaryWeeklyEmployee(message: string, customerCode: number, dateFrom: Date, dateTo: Date, employeeId: number) {
    const from = this.datepipe.transform(dateFrom, 'yyyy-MM-dd');
    const to = this.datepipe.transform(dateTo, 'yyyy-MM-dd');
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/GetSummaryWeeklyEmployee?customerCode=${customerCode}&dateFrom=${from}&dateTo=${to}&employeeId=${employeeId}`,
      message
    );
  }

  getTimecardEntryOptions(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/GetEntryOptions?customerCode=${customerCode}`,
      message, undefined, true, null, true
    );
  }

  update(message: string, customerCode: number, body: any, quiet = false) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/Update?customerCode=${customerCode}`,
      body,
      message, undefined, true, null, quiet
    );
  }

  deleteTimecard(message: string, customerCode: number, timecardId: number, quiet = false) {
    return this.delete(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/Delete?customerCode=${customerCode}&timecardId=${timecardId}`,
      message, undefined, true, null, quiet
    );
  }

  getJobsByEmployee(
    message: string,
    customerCode: number,
    employeeId: number
  ) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployeeJobAssignment/GetByEmployee?customerCode=${customerCode}&employeeId=${employeeId}`,
      message
    );
  }

  saveSkillLevels(message: string, customerCode: number, body: any) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployeeJobAssignment/SaveSkillLevels?customerCode=${customerCode}`,
      body,
      message
    );
  }

  assignJob(message: string, customerCode: number, body: any) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployeeJobAssignment/Assign?customerCode=${customerCode}`,
      body,
      message
    );
  }

  getAllJobs(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/GetAll?customerCode=${customerCode}`,
      message
    );
  }

  // quiet: no global error toast - used where the caller answers JOB_IN_OTHER_DEPARTMENT itself
  // (FENIKS US-4.102: the error carries details, the user is asked "same job / a different job here").
  saveJobTitle(message: string, customerCode: number, body: any, quiet: boolean = false) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/Save?customerCode=${customerCode}`,
      body,
      message,
      undefined,
      true,
      null,
      quiet
    );
  }

  // FENIKS US-4.106 - the one way a job enters TLM: jobs awaiting assignment go into a
  // department. Lenient batch: skipped[] carries a reason per job (JOB_IN_OTHER_DEPARTMENT
  // with the catalog job to ask about). items: { jobTitleId, catalogJobId?, newJobHere? }.
  admitJobs(message: string, customerCode: number, departmentId: number, items: any[]) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/Admit?customerCode=${customerCode}`,
      { departmentId, items },
      message
    );
  }

  // FENIKS US-4.107 - the way a job leaves TLM: jobs unticked in a department are awaiting
  // assignment again. ids = tlmJobTitle ids (GetLocationJobs item.id). Lenient batch: skipped[]
  // carries a reason per job (OPEN_TIMECARD with employees, UPCOMING_SHIFTS with shiftCount /
  // lastShiftDate, NOT_IN_TLM).
  withdrawJobs(message: string, customerCode: number, ids: number[]) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/Withdraw?customerCode=${customerCode}`,
      { ids },
      message
    );
  }

  getAvailableFromCatalog(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/GetAvailableFromCatalog?customerCode=${customerCode}`,
      message
    );
  }

  subscribeFromCatalog(
    message: string,
    customerCode: number,
    jobCatalogIds: number[],
    defaultHourlyRate: number | null
  ) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/Subscribe?customerCode=${customerCode}`,
      { jobCatalogIds, defaultHourlyRate },
      message
    );
  }

  // FENIKS US-4.106 - Jobs tab: take a job of the location off the assignment list, and bring
  // it back. jobTitleId is JobTitle.Pk_Id (LocationJob.jobTitleId), not the TLM job id.
  excludeJob(message: string, customerCode: number, jobTitleId: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/Exclude?customerCode=${customerCode}&jobTitleId=${jobTitleId}`,
      null,
      message
    );
  }

  includeJob(message: string, customerCode: number, jobTitleId: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/Include?customerCode=${customerCode}&jobTitleId=${jobTitleId}`,
      null,
      message
    );
  }

  // FENIKS US-4.106 (AC-11) - whether a job in TLM (id = tlmJobTitle) can move to another
  // department from the Jobs tab: it moves with its catalog job, only when that has no data in
  // the group. Read only.
  canMoveJob(message: string, customerCode: number, id: number, departmentId: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/CanMove?customerCode=${customerCode}&id=${id}&departmentId=${departmentId}`,
      message
    );
  }

  // FENIKS US-4.106 (AC-11) - the safe move itself (JOB_MOVE_NOT_SAFE when it is not safe).
  moveJob(message: string, customerCode: number, id: number, departmentId: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/Move?customerCode=${customerCode}&id=${id}&departmentId=${departmentId}`,
      null,
      message
    );
  }

  // US-3.7 OH and Shifts tab - Cutoff (sekcija 1) ide kroz Config endpoint.
  // Save body je ceo ConfigModel (passthrough nepromenjenih polja).
  getConfig(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/Config/GetConfig?customerCode=${customerCode}`,
      message
    );
  }

  saveConfig(message: string, customerCode: number, body: any) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/Config/SaveConfig?customerCode=${customerCode}`,
      body,
      message
    );
  }

  /**
   * Saves only the fields a uClock Setup tab owns. SaveConfig takes the whole document
   * and four tabs write it, each from the copy it loaded; merging onto that copy puts
   * back whatever another tab saved in the meantime. So the config is read again right
   * before the save and the tab's fields go on top. If that read fails nothing is saved.
   * quiet = the caller shows its own error toast.
   */
  saveConfigFields(message: string, customerCode: number, fields: any, quiet = false) {
    const route = `gstlm/Config/GetConfig?customerCode=${customerCode}`;
    const read = quiet ? this.sget(route, message) : this.get(SvcClients.ResourceSvc, route, message);
    return read.then((resp: any) => {
      // null = no config row yet; the save creates it from these fields.
      const body = { ...(resp?.result ?? {}), ...fields };
      return quiet
        ? this.spost(`gstlm/Config/SaveConfig?customerCode=${customerCode}`, body, message)
        : this.saveConfig(message, customerCode, body);
    });
  }

  // US-3.7 OH and Shifts tab - Sekcija 2 Operating Hours.
  // GET vraca 7 redova (DayOfWeek 0=Sun..6=Sat). Save prima ceo array 7 redova;
  // server replace-uje sve redove (truth = klijent body).
  getOperatingHours(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMOperatingHours/GetByCustomer?customerCode=${customerCode}`,
      message
    );
  }

  saveOperatingHours(message: string, customerCode: number, body: any[]) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMOperatingHours/Save?customerCode=${customerCode}`,
      body,
      message
    );
  }

  /** FENIKS S9: the location's jobs the caller may schedule (their departments), with department. */
  getScheduleJobs(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMSchedule/GetJobs?customerCode=${customerCode}`,
      message
    );
  }

  getShiftTemplates(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMShiftTemplate/GetAll?customerCode=${customerCode}`,
      message
    );
  }

  // US-3.7 OH and Shifts tab - Sekcija 3 Shifts.
  // Save: Id=null/0 = insert, Id>0 = update. Body je ceo ShiftTemplateModel.
  // Deactivate: postavlja Active=0 (soft delete) - id ide kao query param.
  // The shift templates card shows its own error per failure, so these stay quiet (S8 error model below).
  saveShiftTemplate(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMShiftTemplate/Save?customerCode=${customerCode}`, body, message);
  }

  deactivateShiftTemplate(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMShiftTemplate/Deactivate?customerCode=${customerCode}&id=${id}`, null, message);
  }

  managerClockOut(message: string, customerCode: number, body: any, quiet = false) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMClock/ManagerClockOut?customerCode=${customerCode}`,
      body,
      message, undefined, true, null, quiet
    );
  }


  create(message: string, customerCode: number, body: any, quiet = false) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMTimecard/Create?customerCode=${customerCode}`,
      body,
      message, undefined, true, null, quiet
    );
  }


  getTeam(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployee/GetTeam?customerCode=${customerCode}`,
      message
    );
  }

  getEmployeesByCustomer(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployee/GetByCustomer?customerCode=${customerCode}`,
      message
    );
  }

  bulkImportEmployees(message: string, customerCode: number, rows: any[]) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployee/BulkImport?customerCode=${customerCode}`,
      { rows },
      message
    );
  }

  createEmployee(message: string, customerCode: number, body: any) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployee/Create?customerCode=${customerCode}`,
      body,
      message
    );
  }

  updateEmployee(message: string, customerCode: number, body: any) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployee/Update?customerCode=${customerCode}`,
      body,
      message
    );
  }

  getEmployeeTlmStatus(
    message: string,
    customerCode: number,
    employeeKeys: string[]
  ) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployee/GetTlmStatus?customerCode=${customerCode}`,
      { employeeKeys },
      message
    );
  }

  importEmployeesFromPos(
    message: string,
    customerCode: number,
    employeeKeys: string[]
  ) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMEmployee/ImportFromPOS?customerCode=${customerCode}`,
      { employeeKeys },
      message
    );
  }

  getPendingApprovals(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMApproval/GetPending?customerCode=${customerCode}`,
      message
    );
  }

  approveClockIn(message: string, customerCode: number, clockInRequestId: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMApproval/Approve?customerCode=${customerCode}`,
      { clockInRequestId },
      message
    );
  }

  rejectClockIn(message: string, customerCode: number, clockInRequestId: number, reason: string | null) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMApproval/Reject?customerCode=${customerCode}`,
      { clockInRequestId, reason: reason || null },
      message
    );
  }

  getDepartments(message: string) {
    return this.get(SvcClients.ResourceSvc, `gstlm/TLMDepartment/GetAll`, message);
  }

  saveDepartment(
    message: string,
    body: { id: number | null; name: string; code: string | null; description: string | null }
  ) {
    return this.post(SvcClients.ResourceSvc, `gstlm/TLMDepartment/Save`, body, message);
  }

  removeDepartment(message: string, id: number) {
    return this.post(SvcClients.ResourceSvc, `gstlm/TLMDepartment/Remove?id=${id}`, null, message);
  }

  // FENIKS US-4.101 - new GROUP department started from a location's Departments tab:
  // created in the group catalog and activated on that location in one call. Behind
  // uClock Group Setup on the proxy (TLMDepartment key). Saved in one transaction on the server.
  // description = group description; revenueCenter / localDescription / active = the instance.
  createAndActivateDepartment(
    message: string,
    customerCode: number,
    body: {
      name: string;
      code: string;
      description: string | null;
      revenueCenter: string | null;
      localDescription: string | null;
      active: boolean;
      // FENIKS US-4.102: built-in area or an active custom area of the location.
      houseAreaId?: number | null;
    }
  ) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMDepartment/CreateAndActivate?customerCode=${customerCode}`,
      body,
      message
    );
  }

  // -- FENIKS US-4.102 - house areas of a location (uClock Setup) -------------
  // Built-in FOH / BOH / Support / Other plus the location's own custom areas. Create with
  // a label the location already has returns that area (a deactivated one comes back).

  getHouseAreas(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMHouseArea/GetAll?customerCode=${customerCode}`,
      message
    );
  }

  createHouseArea(message: string, customerCode: number, label: string) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMHouseArea/Create?customerCode=${customerCode}`,
      { label },
      message
    );
  }

  renameHouseArea(message: string, customerCode: number, id: number, label: string) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMHouseArea/Rename?customerCode=${customerCode}&id=${id}`,
      { label },
      message
    );
  }

  deactivateHouseArea(message: string, customerCode: number, id: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMHouseArea/Deactivate?customerCode=${customerCode}&id=${id}`,
      null,
      message
    );
  }

  // -- FENIKS US-4.101 - departments at a location (uClock Setup) -------------
  // A location uses group departments; it never creates its own. The instance
  // carries only local details: local name (alias), Revenue Center, description, Active.

  // FENIKS US-4.106 - every job of the location with its state (IN_TLM / AWAITING / EXCLUDED),
  // hasPos and the awaiting count (the Departments banner). quiet: no global error toast
  // (the banner just hides when the call fails).
  getLocationJobs(message: string, customerCode: number, quiet: boolean = false) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobTitle/GetLocationJobs?customerCode=${customerCode}`,
      message,
      undefined,
      true,
      null,
      quiet
    );
  }

  getLocationDepartments(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/GetAll?customerCode=${customerCode}`,
      message
    );
  }

  // The group whose catalog the Add Department search looks in (shown in the dialog).
  getLocationDepartmentGroup(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/GetGroup?customerCode=${customerCode}`,
      message,
      undefined,
      true,
      null,
      true
    );
  }

  // Typeahead over the group catalog (group name, code, other locations' local names).
  // Blank term = whole catalog. Quiet: a failed keystroke must not raise a toast.
  searchDepartments(message: string, customerCode: number, term: string) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/Search?customerCode=${customerCode}&term=${encodeURIComponent(term || '')}`,
      message,
      undefined,
      true,
      null,
      true
    );
  }

  // "Activate here": instance only. The body (optional on the server) carries the local details.
  activateDepartment(
    message: string,
    customerCode: number,
    departmentId: number,
    details: { revenueCenter: string | null; description: string | null; active: boolean; houseAreaId?: number | null }
  ) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/Activate?customerCode=${customerCode}&departmentId=${departmentId}`,
      details,
      message
    );
  }

  saveLocationDepartment(
    message: string,
    customerCode: number,
    body: {
      id: number;
      localName: string | null;
      revenueCenter: string | null;
      description: string | null;
      active: boolean;
      // FENIKS US-4.102: not sent = the department keeps its area (the card's Activate / Deactivate).
      houseAreaId?: number | null;
    }
  ) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/Save?customerCode=${customerCode}`,
      body,
      message
    );
  }

  removeLocationDepartment(message: string, customerCode: number, id: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/Remove?customerCode=${customerCode}&id=${id}`,
      null,
      message
    );
  }

  // FENIKS US-4.104 - the "Apply to" picker of the department modal: uClock locations of this
  // location's group the caller may set up, this one excluded. Quiet: an empty list only
  // disables the option.
  getApplyTargets(message: string, customerCode: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/GetApplyTargets?customerCode=${customerCode}`,
      message,
      undefined,
      true,
      null,
      true
    );
  }

  // FENIKS US-4.103 - the "+ Add manager" list of a department card: users of this location who
  // may be its managers and are not yet. id = the department instance. Quiet: an empty list only
  // shows the hint on the card.
  getManagerCandidates(message: string, customerCode: number, id: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/GetManagerCandidates?customerCode=${customerCode}&id=${id}`,
      message,
      undefined,
      true,
      null,
      true
    );
  }

  // FENIKS US-4.103 - userCode = the person (User.Code). Both return the department's managers.
  addDepartmentManager(message: string, customerCode: number, id: number, userCode: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/AddManager?customerCode=${customerCode}&id=${id}&userCode=${userCode}`,
      null,
      message
    );
  }

  removeDepartmentManager(message: string, customerCode: number, id: number, userCode: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/RemoveManager?customerCode=${customerCode}&id=${id}&userCode=${userCode}`,
      null,
      message
    );
  }

  // FENIKS US-4.104 - the department of this location onto other locations (instance, house
  // area, jobs). Lenient per location: HTTP 200 with a result per location.
  applyDepartmentToLocations(
    message: string,
    customerCode: number,
    departmentId: number,
    body: { customerCodes: number[]; allLocations: boolean }
  ) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMLocationDepartment/ApplyToLocations?customerCode=${customerCode}&departmentId=${departmentId}`,
      body,
      message
    );
  }

  getJobCatalog(message: string) {
    return this.get(SvcClients.ResourceSvc, `gstlm/TLMJobCatalog/GetAll`, message);
  }

  saveJobCatalog(
    message: string,
    body: {
      id: number | null;
      title: string;
      code: string | null;
      departmentId: number;
      defaultHourlyRate: number | null;
      isTipped: boolean;
    }
  ) {
    return this.post(SvcClients.ResourceSvc, `gstlm/TLMJobCatalog/Save`, body, message);
  }

  removeJobCatalog(message: string, id: number) {
    return this.post(SvcClients.ResourceSvc, `gstlm/TLMJobCatalog/Remove?id=${id}`, null, message);
  }

  // FENIKS US-4.105 - what moving a catalog job to another department would do (read only):
  // locations affected, where the department is activated, and what refuses the move.
  getJobMoveImpact(message: string, jobCatalogId: number, departmentId: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobCatalog/GetMoveImpact?jobCatalogId=${jobCatalogId}&departmentId=${departmentId}`,
      message
    );
  }

  // FENIKS US-4.105 - the move itself, at every location, logged. Save no longer changes the
  // department of an existing job (JOB_MOVE_REQUIRED).
  moveCatalogJob(message: string, jobCatalogId: number, departmentId: number) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobCatalog/Move?jobCatalogId=${jobCatalogId}&departmentId=${departmentId}`,
      null,
      message
    );
  }

  // FENIKS US-4.105 - the change history of a department / a catalog job, newest first.
  getDepartmentChangeLog(message: string, departmentId: number) {
    return this.get(SvcClients.ResourceSvc, `gstlm/TLMDepartment/GetChangeLog?departmentId=${departmentId}`, message);
  }

  getJobChangeLog(message: string, jobCatalogId: number) {
    return this.get(SvcClients.ResourceSvc, `gstlm/TLMJobCatalog/GetChangeLog?jobCatalogId=${jobCatalogId}`, message);
  }

  // FENIKS US-4.105 - pairs of departments that look like the same one. Quiet: the list is a
  // suggestion under the department table; when it fails it is simply not shown.
  getMergeCandidates(message: string) {
    return this.get(SvcClients.ResourceSvc, `gstlm/TLMDepartment/GetMergeCandidates`, message, undefined, true, null, true);
  }

  // FENIKS US-4.105 - what merging otherId into canonicalId would do (read only).
  getMergePreview(message: string, canonicalId: number, otherId: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `gstlm/TLMDepartment/GetMergePreview?canonicalId=${canonicalId}&otherId=${otherId}`,
      message
    );
  }

  // FENIKS US-4.105 - the merge: keep = whose instance survives at every location that uses both.
  mergeDepartments(
    message: string,
    body: { canonicalId: number; otherId: number; keep: { customerCode: number; keep: 'CANONICAL' | 'OTHER' }[] }
  ) {
    return this.post(SvcClients.ResourceSvc, `gstlm/TLMDepartment/Merge`, body, message);
  }

  getGroupLocations(message: string) {
    return this.get(SvcClients.ResourceSvc, `gstlm/TLMJobCatalog/GetGroupLocations`, message);
  }

  // Lenient batch (N jobs x M locations): HTTP 200 with skipped[] is a normal outcome.
  applyToLocations(message: string, jobCatalogIds: number[], customerCodes: number[]) {
    return this.post(
      SvcClients.ResourceSvc,
      `gstlm/TLMJobCatalog/ApplyToLocations`,
      { jobCatalogIds, customerCodes },
      message
    );
  }

  // -- Stream S8 - error model ----------------------------------------------
  // The scheduling controls render their own error toast (they need the server's
  // business message and the 409 reload, US-8.24), so the calls below ask the
  // ErrorInterceptor to stay quiet: one toast per failure, not two. Same
  // arrangement as insightsV2.client.ts.

  private sget(route: string, message?: string) {
    return this.get(SvcClients.ResourceSvc, route, message, undefined, true, null, true);
  }

  private spost(route: string, body: any, message?: string) {
    return this.post(SvcClients.ResourceSvc, route, body, message, undefined, true, null, true);
  }

  // -- Stream S8, US-8.3 - scheduling master data --------------------------
  // Shift slots (staffing requirements per shift template)

  getShiftSlots(message: string, customerCode: number, shiftTemplateId: number) {
    return this.sget(`gstlm/TLMShiftSlot/GetByShiftTemplate?customerCode=${customerCode}&shiftTemplateId=${shiftTemplateId}`, message);
  }

  saveShiftSlot(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMShiftSlot/Save?customerCode=${customerCode}`, body, message);
  }

  removeShiftSlot(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMShiftSlot/Remove?customerCode=${customerCode}&id=${id}`, null, message);
  }

  // Employee availability (weekly windows; no rows = available 24/7)

  getEmployeeAvailability(message: string, customerCode: number, employeeId: number) {
    return this.sget(`gstlm/TLMEmployeeAvailability/GetByEmployee?customerCode=${customerCode}&employeeId=${employeeId}`, message);
  }

  saveEmployeeAvailability(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMEmployeeAvailability/Save?customerCode=${customerCode}`, body, message);
  }

  removeEmployeeAvailability(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMEmployeeAvailability/Remove?customerCode=${customerCode}&id=${id}`, null, message);
  }

  // Employee time off (status: 0=Pending, 1=Approved, 2=Rejected)

  getTimeOffReasons(message: string, customerCode: number) {
    return this.sget(`gstlm/TLMEmployeeTimeOff/GetReasons?customerCode=${customerCode}`, message);
  }

  /** Location-wide time off: pending plus anything not yet over (or everything with includePast). */
  getAllTimeOff(message: string, customerCode: number, includePast: boolean) {
    return this.sget(`gstlm/TLMEmployeeTimeOff/GetAll?customerCode=${customerCode}&includePast=${includePast}`, message);
  }

  getEmployeeTimeOff(message: string, customerCode: number, employeeId: number) {
    return this.sget(`gstlm/TLMEmployeeTimeOff/GetByEmployee?customerCode=${customerCode}&employeeId=${employeeId}`, message);
  }

  saveEmployeeTimeOff(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMEmployeeTimeOff/Save?customerCode=${customerCode}`, body, message);
  }

  approveEmployeeTimeOff(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMEmployeeTimeOff/Approve?customerCode=${customerCode}&id=${id}`, null, message);
  }

  rejectEmployeeTimeOff(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMEmployeeTimeOff/Reject?customerCode=${customerCode}&id=${id}`, null, message);
  }

  removeEmployeeTimeOff(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMEmployeeTimeOff/Remove?customerCode=${customerCode}&id=${id}`, null, message);
  }

  // Employee scheduling profile (the 8 scheduling columns on tlmEmployee)

  getEmployeeSchedulingProfile(message: string, customerCode: number, employeeId: number) {
    return this.sget(`gstlm/TLMEmployeeScheduling/GetProfile?customerCode=${customerCode}&employeeId=${employeeId}`, message);
  }

  saveEmployeeSchedulingProfile(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMEmployeeScheduling/SaveProfile?customerCode=${customerCode}`, body, message);
  }

  // -- Stream S8, US-8.4/8.5/8.6 - work schedule (drafts, solver, gaps, publish)

  getScheduleWeek(message: string, customerCode: number, weekStart: string) {
    return this.sget(`gstlm/TLMSchedule/GetWeek?customerCode=${customerCode}&weekStart=${weekStart}`, message);
  }

  /** Weeks with a schedule (draft or published) between two dates - Copy week's sources (SCR-022). */
  getScheduleWeeks(message: string, customerCode: number, from: string, to: string) {
    return this.sget(`gstlm/TLMSchedule/GetWeeks?customerCode=${customerCode}&from=${from}&to=${to}`, message);
  }

  /** Copy an earlier week into the draft that exists (US-8.108). */
  copyIntoDraft(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/CopyIntoDraft?customerCode=${customerCode}`, body, message);
  }

  /** Clear the draft: every seat (locked ones kept on request); shifts stay. */
  clearDraft(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/ClearDraft?customerCode=${customerCode}`, body, message);
  }

  createScheduleDraft(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/CreateDraft?customerCode=${customerCode}`, body, message);
  }

  rebuildScheduleCoverage(message: string, customerCode: number, weekStart: string) {
    return this.spost(`gstlm/TLMSchedule/RebuildCoverage?customerCode=${customerCode}&weekStart=${weekStart}`, null, message);
  }

  runScheduleSolver(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/RunSolver?customerCode=${customerCode}`, body, message);
  }

  getLatestScheduleJob(message: string, customerCode: number, weekStart: string) {
    return this.sget(`gstlm/TLMSchedule/GetLatestJob?customerCode=${customerCode}&weekStart=${weekStart}`, message);
  }

  getScheduleCandidates(message: string, customerCode: number, shiftInstanceId: number, jobTitleId: number) {
    return this.sget(`gstlm/TLMSchedule/GetCandidates?customerCode=${customerCode}&shiftInstanceId=${shiftInstanceId}&jobTitleId=${jobTitleId}`, message);
  }

  assignScheduleSeat(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/Assign?customerCode=${customerCode}`, body, message);
  }

  unassignScheduleSeat(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMSchedule/Unassign?customerCode=${customerCode}&id=${id}`, null, message);
  }

  /** Seats one job needs on one shift (REQ-0703) - a party, "+ Extra", a shorter night. */
  setShiftSeats(message: string, customerCode: number, body: { shiftInstanceId: number; jobTitleId: number; requiredCount: number; reason?: string | null }) {
    return this.spost(`gstlm/TLMSchedule/SetSeats?customerCode=${customerCode}`, body, message);
  }

  /** Take people off a published shift and tell them; closeSeats lowers the seats too (a cancelled dinner). */
  releaseShiftSeats(message: string, customerCode: number, body: { assignmentIds: number[]; closeSeats: boolean; reason: string | null }) {
    return this.spost(`gstlm/TLMSchedule/ReleaseSeats?customerCode=${customerCode}`, body, message);
  }

  unassignScheduleSeatsBulk(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/UnassignBulk?customerCode=${customerCode}`, body, message);
  }

  swapScheduleAssignments(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/Swap?customerCode=${customerCode}`, body, message);
  }

  setScheduleAssignmentFrozen(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/SetFrozen?customerCode=${customerCode}`, body, message);
  }

  /** Lock / unlock many assignments of a draft week (filters AND-ed; none = whole week). */
  setScheduleFrozenBulk(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/SetFrozenBulk?customerCode=${customerCode}`, body, message);
  }

  getUncoveredSlots(message: string, customerCode: number, weekStart: string) {
    return this.sget(`gstlm/TLMSchedule/GetUncovered?customerCode=${customerCode}&weekStart=${weekStart}`, message);
  }

  explainUncoveredSlot(message: string, customerCode: number, id: number) {
    return this.sget(`gstlm/TLMSchedule/ExplainUncovered?customerCode=${customerCode}&id=${id}`, message);
  }

  acknowledgeUncoveredSlot(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMSchedule/Acknowledge?customerCode=${customerCode}&id=${id}`, null, message);
  }

  acknowledgeAllOpenSlots(message: string, customerCode: number, weekStart: string) {
    return this.spost(`gstlm/TLMSchedule/AcknowledgeAllOpen?customerCode=${customerCode}&weekStart=${weekStart}`, null, message);
  }

  mitigateUncoveredSlot(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/Mitigate?customerCode=${customerCode}`, body, message);
  }

  getSchedulePreflight(message: string, customerCode: number) {
    return this.sget(`gstlm/TLMSchedule/Preflight?customerCode=${customerCode}`, message);
  }

  // -- S8: availability by shift (tlmEmployeeShiftAvailability, FENIKS 1.1 C11) --
  /** One line per active member: days open, shifts ticked, hours blocked (Weekly Availability table). */
  getShiftAvailabilitySummary(message: string, customerCode: number) {
    return this.sget(`gstlm/TLMEmployeeShiftAvailability/GetSummary?customerCode=${customerCode}`, message);
  }

  /** The usual pattern, or (validFrom + validTo) the set that applies only on those dates (S8/111). */
  getEmployeeShiftAvailability(message: string, customerCode: number, employeeId: number, validFrom: string | null = null, validTo: string | null = null) {
    const range = validFrom && validTo ? `&validFrom=${validFrom}&validTo=${validTo}` : '';
    return this.sget(`gstlm/TLMEmployeeShiftAvailability/GetGrid?customerCode=${customerCode}&employeeId=${employeeId}${range}`, message);
  }

  saveEmployeeShiftAvailability(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMEmployeeShiftAvailability/SaveGrid?customerCode=${customerCode}`, body, message);
  }

  // -- S8 US-8.12: open-shift offers (tlmShiftOffer) --
  /** No week: every offer still open on a published week (Requests inbox). */
  getShiftOffers(message: string, customerCode: number, weekStart: string | null, status: number | null) {
    const weekPart = weekStart ? `&weekStart=${weekStart}` : '';
    const statusPart = status === null ? '' : `&status=${status}`;
    return this.sget(`gstlm/TLMShiftOffer/GetOffers?customerCode=${customerCode}${weekPart}${statusPart}`, message);
  }

  getShiftOfferCandidates(
    message: string,
    customerCode: number,
    shiftInstanceId: number,
    jobTitleId: number
  ) {
    return this.sget(`gstlm/TLMShiftOffer/GetCandidates?customerCode=${customerCode}&shiftInstanceId=${shiftInstanceId}&jobTitleId=${jobTitleId}`, message);
  }

  createShiftOffer(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMShiftOffer/Create?customerCode=${customerCode}`, body, message);
  }

  respondToShiftOffer(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMShiftOffer/Respond?customerCode=${customerCode}`, body, message);
  }

  cancelShiftOffer(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMShiftOffer/Cancel?customerCode=${customerCode}&id=${id}`, null, message);
  }

  // -- S8 US-8.15: bid windows (tlmBidWindow) --
  /** No week: every open window on a published week (Requests inbox). */
  getBidWindows(message: string, customerCode: number, weekStart: string | null, status: number | null) {
    const weekPart = weekStart ? `&weekStart=${weekStart}` : '';
    const statusPart = status === null ? '' : `&status=${status}`;
    return this.sget(`gstlm/TLMBidWindow/GetWindows?customerCode=${customerCode}${weekPart}${statusPart}`, message);
  }

  createBidWindow(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMBidWindow/Create?customerCode=${customerCode}`, body, message);
  }

  placeBid(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMBidWindow/PlaceBid?customerCode=${customerCode}`, body, message);
  }

  withdrawBid(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMBidWindow/WithdrawBid?customerCode=${customerCode}`, body, message);
  }

  awardBidWindow(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMBidWindow/Award?customerCode=${customerCode}`, body, message);
  }

  cancelBidWindow(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMBidWindow/Cancel?customerCode=${customerCode}&id=${id}`, null, message);
  }

  // -- S8 US-8.8: approvals inbox (tlmScheduleRequest) --
  getScheduleRequests(
    message: string,
    customerCode: number,
    weekStart: string | null,
    status: number | null
  ) {
    // No week: everything still waiting on a manager at the location (same count as TLMToday/Badges).
    const weekPart = weekStart ? `&weekStart=${weekStart}` : '';
    const statusPart = status === null ? '' : `&status=${status}`;
    return this.sget(`gstlm/TLMScheduleRequest/GetRequests?customerCode=${customerCode}${weekPart}${statusPart}`, message);
  }

  createScheduleRequest(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMScheduleRequest/Create?customerCode=${customerCode}`, body, message);
  }

  decideScheduleRequest(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMScheduleRequest/Decide?customerCode=${customerCode}`, body, message);
  }

  /** REQ-0415: approved or denied time off back to pending. */
  reopenScheduleRequest(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMScheduleRequest/Reopen?customerCode=${customerCode}`, body, message);
  }

  cancelScheduleRequest(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMScheduleRequest/Cancel?customerCode=${customerCode}&id=${id}`, null, message);
  }

  /** US-8.109: changes to the published week since publish. */
  getChangesSincePublish(message: string, customerCode: number, weekStart: string) {
    return this.sget(`gstlm/TLMSchedule/GetChangesSincePublish?customerCode=${customerCode}&weekStart=${weekStart}`, message);
  }

  // -- Operating Calendar (US-8.104) --

  getCalendarEntries(message: string, customerCode: number) {
    return this.sget(`gstlm/TLMOperatingCalendar/GetEntries?customerCode=${customerCode}`, message);
  }

  getCalendarHolidays(message: string, customerCode: number) {
    return this.sget(`gstlm/TLMOperatingCalendar/GetHolidays?customerCode=${customerCode}`, message);
  }

  saveCalendarEntry(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMOperatingCalendar/Save?customerCode=${customerCode}`, body, message);
  }

  removeCalendarEntry(message: string, customerCode: number, id: number) {
    return this.spost(`gstlm/TLMOperatingCalendar/Remove?customerCode=${customerCode}&id=${id}`, null, message);
  }

  // -- Day of operations (FENIKS Wave 1: roster, call-out, on-call, standby) --

  getTodayRoster(message: string, customerCode: number, date: string | null) {
    const datePart = date ? `&date=${date}` : '';
    return this.sget(`gstlm/TLMToday/Roster?customerCode=${customerCode}${datePart}`, message);
  }

  /** Dashboard (FENIKS US-10.1): getActiveTimecards without the global error toast - a failure stays in its tile. */
  getActiveTimecardsQuiet(customerCode: number) {
    return this.sget(`gstlm/TLMTimecard/GetActive?customerCode=${customerCode}`, '');
  }

  /** Dashboard (FENIKS US-10.1): getConfig without the global error toast. */
  getConfigQuiet(customerCode: number) {
    return this.sget(`gstlm/Config/GetConfig?customerCode=${customerCode}`, '');
  }

  /** Dashboard (FENIKS US-10.1): getPendingApprovals without the global error toast. */
  getPendingApprovalsQuiet(customerCode: number) {
    return this.sget(`gstlm/TLMApproval/GetPending?customerCode=${customerCode}`, '');
  }

  // -- Alarms (FENIKS US-10M.1 contract; PDP dashboard US-10.1, notification panel US-10.5) --

  /** Open alarms of the location, newest first: [{id, type, severity, employeeId, employeeName, occurredAt, params, message}]. */
  getAlerts(customerCode: number) {
    return this.sget(`gstlm/TLMAlert/Get?customerCode=${customerCode}`, '');
  }

  /** Labor today (US-10M.1 contract; PDP dashboard US-10.1). No content (204) - do not show it. */
  getLaborToday(customerCode: number) {
    return this.sget(`gstlm/TLMLabor/GetToday?customerCode=${customerCode}`, '');
  }

  // -- FENIKS US-11.1 - uClock Reports tab (TLMReport, uClock Reports) ----------
  // Weeks, results and the CSV all come from the server; the tab computes nothing.

  /** Weeks of the picker (location's work-week start), the current one and the delivered report keys. */
  getReportWeeks(customerCode: number) {
    return this.sget(`gstlm/TLMReport/GetWeeks?customerCode=${customerCode}`, '');
  }

  /** One report for one week: kpi, columns, rows, total, emptyMessage. */
  getReport(customerCode: number, report: string, weekStart: string) {
    return this.sget(
      `gstlm/TLMReport/Get?customerCode=${customerCode}&report=${encodeURIComponent(report)}&weekStart=${weekStart}`, '');
  }

  /** Saves the report's CSV under the server's file name; rejects with the TLM error on failure. */
  getReportCsv(customerCode: number, report: string, weekStart: string) {
    return this.downloadFileOrReject(
      SvcClients.ResourceSvc,
      `gstlm/TLMReport/GetCsv?customerCode=${customerCode}&report=${encodeURIComponent(report)}&weekStart=${weekStart}`,
      `uClock-${report}-${weekStart}.csv`);
  }

  getTodayBadges(message: string, customerCode: number) {
    return this.sget(`gstlm/TLMToday/Badges?customerCode=${customerCode}`, message);
  }

  callOut(message: string, customerCode: number, body: { employeeId: number; assignmentId?: number | null; reason: string | null }) {
    return this.spost(`gstlm/TLMToday/CallOut?customerCode=${customerCode}`, body, message);
  }

  getStandbyDay(message: string, customerCode: number, date: string) {
    return this.sget(`gstlm/TLMStandby/GetDay?customerCode=${customerCode}&date=${date}`, message);
  }

  /** action: OnCall/Cut | OnCall/CallIn | OnCall/Release | Plan | Request | CallIn | Arrived | Remove */
  standbyAction(message: string, customerCode: number, action: string, body: any) {
    return this.spost(`gstlm/TLMStandby/${action}?customerCode=${customerCode}`, body, message);
  }

  /** The audit viewer: the week, or (weekStart null) the whole location for the last 30 days; filters optional. */
  getScheduleAudit(message: string, customerCode: number, weekStart: string | null,
    filters: { action?: string | null; search?: string | null; aiOnly?: boolean } = {}) {
    const q = [`customerCode=${customerCode}`];
    if (weekStart) q.push(`weekStart=${weekStart}`);
    if (filters.action) q.push(`action=${encodeURIComponent(filters.action)}`);
    if (filters.search) q.push(`search=${encodeURIComponent(filters.search)}`);
    if (filters.aiOnly) q.push('aiOnly=true');
    return this.sget(`gstlm/TLMSchedule/GetAudit?${q.join('&')}`, message);
  }

  /** FENIKS AC-0604: the published week back to draft. */
  unpublishSchedule(message: string, customerCode: number, body: { weekStart: string; reason: string | null }) {
    return this.spost(`gstlm/TLMSchedule/Unpublish?customerCode=${customerCode}`, body, message);
  }

  publishSchedule(message: string, customerCode: number, body: any) {
    return this.spost(`gstlm/TLMSchedule/Publish?customerCode=${customerCode}`, body, message);
  }
}
